import React, { useState, useEffect } from 'react';
import { Alert, Button, Form, HistoryPanel, ListGroup, StatusBadge, Tab, Tabs } from '@/components/ui';
import { Task } from '../types/Task';
import { taskService as regularTaskService } from '../services/taskService';
import { Project } from '../types/Project';
import ResponsiveModal from '../features/quantified-tracking/components/ResponsiveModal';
import { getFieldLockingState, getLockedFieldStyle, canEditInternalReview } from '../utils/fieldLocking';
import { usePermissions } from '../hooks/usePermissions';
import { TaskStage } from '../types/Task';
import { attachmentService } from '../services/attachmentService';
import type { TaskAttachment } from '../types/Task';
import TaskImageAttachments from '../features/quantified-tracking/components/taskModals/TaskImageAttachments';
import formAttachmentService, { FormAttachment } from '../services/formAttachmentService';
import { taskService as quantifiedTaskService } from '../services/quantifiedTaskService';
import { projectService } from '../services/projectService';
import { useAuth } from '../contexts/AuthContext';
import { getTaskHistoryAttribution } from '../utils/taskHistoryAttribution';

type TaskStatus = 'PROPOSED' | 'PLANNED' | 'ACTIVE' | 'CLOSED' | 'CANCELLED';

// Utility function to format text in sentence case
const formatSentenceCase = (text: string): string => {
  if (!text) return '';
  // Handle underscore case first (e.g., "some_text" -> "Some Text")
  const withoutUnderscores = text.replace(/_/g, ' ');
  // Split into words, capitalize first letter of each word
  return withoutUnderscores
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

interface CustomTaskModalProps {
  visible: boolean;
  task?: Task | null;
  onClose: () => void;
  onSave: (task: Task) => void;
  project?: Project;
  defaultStatus?: string;
}

const CustomTaskModal: React.FC<CustomTaskModalProps> = ({
  visible,
  task,
  onClose,
  onSave,
  project,
  defaultStatus
}) => {
  const { hasPermission } = usePermissions();
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState<'basic' | 'attachments' | 'history'>('basic');
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    description: '',
    status: (defaultStatus as TaskStatus) || 'ACTIVE' as TaskStatus,
    projectId: null as number | null,
  });
  const [projects, setProjects] = useState<Project[]>([]);

  const [attachments, setAttachments] = useState<TaskAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [attachmentNotes, setAttachmentNotes] = useState('');
  const [deletingAttachmentId, setDeletingAttachmentId] = useState<number | null>(null);
  const [formImages, setFormImages] = useState<FormAttachment[]>([]);
  const [newHistoryComment, setNewHistoryComment] = useState('');
  const [isSubmittingHistory, setIsSubmittingHistory] = useState(false);
  const [localHistory, setLocalHistory] = useState(task?.taskHistory || []);

  // Field locking logic based on task state and user permissions
  const canEditInternalReviewPermission = canEditInternalReview(hasPermission);
  
  const fieldLockingState = getFieldLockingState({
    taskStatus: task?.status as TaskStage,
    hasEditInternalReviewPermission: canEditInternalReviewPermission
  });
  
  const isTaskLocked = fieldLockingState.isLocked;
  const lockedFieldStyle = getLockedFieldStyle(isTaskLocked);

  useEffect(() => {
    if (task && visible) {
      setFormData(prev => ({
        ...prev,
        id: task.id.toString(),
        name: task.name || '',
        description: task.description || '',
        status: task.status as TaskStatus,
        projectId: task.projectId ?? project?.id ?? null,
      }));
      // Load attachments for existing task
      loadAttachments(task.id.toString());
      loadFormImages(task.id);
      // Update local history when task changes
      setLocalHistory(task.taskHistory || []);
    } else if (!visible) {
      setFormData({
        id: '',
        name: '',
        description: '',
        status: (defaultStatus as TaskStatus) || 'ACTIVE' as TaskStatus,
        projectId: null,
      });
      setAttachments([]);
      setPendingFile(null);
      setAttachmentNotes('');
      setActiveTab('basic');
      setLocalHistory([]);
      setNewHistoryComment('');
    } else if (!task && visible && project) {
      // When creating new task, use defaultStatus and current project
      setFormData(prev => ({
        ...prev,
        status: (defaultStatus as TaskStatus) || 'ACTIVE' as TaskStatus,
        projectId: project.id,
      }));
    }
  }, [task, visible, defaultStatus, project]);

  useEffect(() => {
    if (!visible) return;
    const loadProjects = async () => {
      try {
        const list = await projectService.getProjectsForTaskModal(undefined, 'active,planned');
        setProjects(list ?? []);
      } catch (err) {
        console.error('Failed to load projects:', err);
        setProjects([]);
      }
    };
    loadProjects();
  }, [visible]);

  const loadAttachments = async (taskId: string) => {
    try {
      const data = await attachmentService.getTaskAttachments(taskId);
      setAttachments(data);
    } catch (err) {
      console.error('Failed to load attachments:', err);
    }
  };

  const loadFormImages = async (taskIdNum: number) => {
    try {
      const imgs = await formAttachmentService.getAttachmentsForTask(taskIdNum);
      setFormImages(imgs || []);
    } catch (err) {
      console.error('Failed to load form images:', err);
      setFormImages([]);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setPendingFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!task?.id || !pendingFile) return;
    setUploading(true);
    try {
      await attachmentService.uploadAttachment(task.id.toString(), pendingFile, attachmentNotes || undefined);
      setPendingFile(null);
      setAttachmentNotes('');
      await loadAttachments(task.id.toString());
    } catch (err) {
      console.error('Failed to upload attachment:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId: number) => {
    if (!attachmentId) return;
    setDeletingAttachmentId(attachmentId);
    try {
      await attachmentService.deleteAttachment(String(attachmentId));
      if (task?.id) {
        await loadAttachments(task.id.toString());
      }
    } catch (err) {
      console.error('Failed to delete attachment:', err);
    } finally {
      setDeletingAttachmentId(null);
    }
  };

  const selectedProjectId = formData.projectId;
  const filteredProjects = React.useMemo(() => {
    const list = projects ?? [];
    const currentId = selectedProjectId ?? project?.id;
    if (!currentId) return list;
    if (list.some(p => p.id === currentId)) return list;
    if (project && project.id === currentId) return [project, ...list];
    return list;
  }, [projects, selectedProjectId, project]);

  const formatProjectLabel = (p: Project) =>
    p.projectNumber ? `${p.name} (#${p.projectNumber})` : p.name;

  const currentProjectName = React.useMemo(() => {
    const id = selectedProjectId ?? project?.id;
    if (!id) return '';
    const p = filteredProjects.find(pr => pr.id === id) ?? project;
    return p ? formatProjectLabel(p) : '';
  }, [filteredProjects, selectedProjectId, project]);

  const handleSave = async () => {
    try {
      const projectIdToSave = formData.projectId ?? project?.id;
      if (!projectIdToSave) {
        setError('Please select a project');
        return;
      }

      if (!formData.name.trim()) {
        setError('Please enter a task name');
        return;
      }

      const payload = {
        description: formData.description,
        name: formData.name,
        type: 'Custom',
        projectId: projectIdToSave,
        status: formData.status,
      };

      let savedTask;
      if (task) {
        savedTask = await regularTaskService.updateTask(task.id.toString(), payload);
      } else {
        savedTask = await regularTaskService.createTask(payload);
      }
      
      setError(null);
      onSave(savedTask);
      onClose();
    } catch (error) {
      console.error(`Failed to ${task ? 'update' : 'create'} custom task:`, error);
      setError(`Failed to ${task ? 'update' : 'create'} custom task`);
    }
  };

  const handleAddHistoryComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newHistoryComment.trim() || !currentUser?.id || !task?.id) return;
    
    if (!hasPermission('quantified:history_create')) return;
    
    try {
      setIsSubmittingHistory(true);
      const updatedTask = await quantifiedTaskService.addHistoryComment(task.id, {
        comments: newHistoryComment.trim(),
        userId: currentUser.id,
        isExternal: false
      });
      
      // Update local history with the new comment
      setLocalHistory(updatedTask.taskHistory || []);
      setNewHistoryComment('');
      
      // Update the task in parent component
      onSave(updatedTask);
    } catch (error) {
      console.error('Error adding history comment:', error);
      alert('Failed to add comment');
    } finally {
      setIsSubmittingHistory(false);
    }
  };

  if (!visible) return null;

  return (
    <ResponsiveModal
      isOpen={visible}
      onClose={onClose}
      title={`${task ? 'Edit' : 'Create'} Custom Task`}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            className="tw-ml-2"
            onClick={handleSave}
            disabled={!formData.name.trim() || isTaskLocked || !(formData.projectId ?? project?.id)}
          >
            {task ? 'Update' : 'Save'}
          </Button>
        </>
      }
    >
      {error && (
        <Alert variant="danger">
          {error}
        </Alert>
      )}
      <Tabs
        activeKey={activeTab}
        onSelect={(k) => k && setActiveTab(k as 'basic' | 'attachments' | 'history')}
        className="tw-mb-3"
      >
        <Tab eventKey="basic" title="Basic Info">
          <form>
            {task && (
              <div className="tw-mb-3">
                <Form.Label>Task ID</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.id}
                  readOnly
                />
              </div>
            )}
            <div className="tw-mb-3">
              <Form.Label>Project</Form.Label>
              {isTaskLocked ? (
                <Form.Control
                  type="text"
                  value={currentProjectName}
                  readOnly
                  style={lockedFieldStyle ?? { backgroundColor: '#e9ecef' }}
                />
              ) : (
                <>
                  <Form.Select
                    value={selectedProjectId ?? ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      projectId: e.target.value ? Number(e.target.value) : null,
                    }))}
                  >
                    <option value="">Select Project</option>
                    {filteredProjects.map(p => (
                      <option key={p.id} value={p.id}>
                        {formatProjectLabel(p)}
                        {project && p.id === project.id ? ' (Current Project)' : ''}
                      </option>
                    ))}
                  </Form.Select>
                  {project && selectedProjectId === project.id && (
                    <small className="tw-text-content-muted tw-block tw-mt-1">
                      Task is on the current project. Change project to move this task.
                    </small>
                  )}
                </>
              )}
            </div>
            <div className="tw-mb-3">
              <Form.Label>Task Name <span className="tw-text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Enter task name..."
                readOnly={isTaskLocked}
                style={isTaskLocked ? lockedFieldStyle : undefined}
              />
            </div>
            <div className="tw-mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={formData.status}
                onChange={(e) => setFormData({...formData, status: e.target.value as TaskStatus})}
                disabled={isTaskLocked}
                style={isTaskLocked ? lockedFieldStyle : undefined}
              >
                {['PROPOSED', 'PLANNED', 'ACTIVE', 'CLOSED', 'CANCELLED'].map(status => (
                  <option key={status} value={status}>{formatSentenceCase(status)}</option>
                ))}
              </Form.Select>
            </div>
            <div className="tw-mb-3">
              <Form.Label>Internal Notes / Description (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows={4}
                placeholder="Enter internal notes or description..."
                readOnly={isTaskLocked}
                style={isTaskLocked ? lockedFieldStyle : undefined}
              />
            </div>
          </form>
        </Tab>
        <Tab eventKey="attachments" title="File Attachments" disabled={!task?.id}>
          {!task?.id ? (
            <Alert variant="info">Save the task first to add attachments.</Alert>
          ) : (
            <>
              <div className="tw-mb-3">
                <Form.Label>Upload New Attachment</Form.Label>
                <Form.Control
                  type="file"
                  className="tw-mb-2"
                  onChange={handleFileSelect}
                  disabled={uploading || isTaskLocked}
                  style={isTaskLocked ? lockedFieldStyle : undefined}
                />
                {pendingFile && (
                  <div className="tw-mb-2 tw-flex tw-gap-2 tw-items-center">
                    <span>Selected: {pendingFile.name}</span>
                  </div>
                )}
                <Form.Control
                  type="text"
                  className="tw-mb-2"
                  placeholder="Notes (optional)"
                  value={attachmentNotes}
                  onChange={(e) => setAttachmentNotes(e.target.value)}
                  disabled={uploading || isTaskLocked}
                  style={isTaskLocked ? lockedFieldStyle : undefined}
                />
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleUpload}
                  disabled={!pendingFile || uploading || isTaskLocked}
                  style={isTaskLocked ? lockedFieldStyle : undefined}
                >
                  {uploading ? 'Uploading...' : 'Upload'}
                </Button>
              </div>

              <TaskImageAttachments 
                attachments={attachments}
                onDeleteAttachment={handleDeleteAttachment}
                isTaskClosed={isTaskLocked}
              />

              {formImages.length > 0 && (
                <div className="tw-mt-4">
                  <h6>Images from Daily Log</h6>
                  <div className="task-images-grid">
                    {formImages.map(img => {
                      const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace('/api', '');
                      const src = `${baseUrl}/api/forms/${img.formId}/attachments/${img.id}/download`;
                      return (
                        <div key={`${img.formId}-${img.id}`} className="task-image-item">
                          <img src={src} alt={img.filename} className="task-image-thumbnail" />
                          <div className="task-image-info">
                            <span className="task-image-filename">{img.filename}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {attachments.filter(a => !(a.attachmentType === 'image' || a.attachmentName.toLowerCase().match(/\.(jpg|jpeg|png|gif|bmp|webp)$/))).length > 0 && (
                <div className="tw-mt-4">
                  <h6>Other Attachments</h6>
                  <ListGroup>
                    {attachments.filter(a => !(a.attachmentType === 'image' || a.attachmentName.toLowerCase().match(/\.(jpg|jpeg|png|gif|bmp|webp)$/))).map(att => (
                      <ListGroup.Item key={att.id} className="tw-flex tw-justify-between tw-items-center">
                        <span>{att.attachmentName}</span>
                        <div>
                          <Button
                            type="button"
                            variant="outline-secondary"
                            size="sm"
                            className="tw-mr-2"
                            onClick={() => attachmentService.downloadAttachment(String(att.id))}
                          >
                            Download
                          </Button>
                          {!isTaskLocked && (
                            <Button
                              type="button"
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteAttachment(att.id ?? 0)}
                              disabled={deletingAttachmentId === att.id}
                            >
                              {deletingAttachmentId === att.id ? 'Deleting...' : 'Delete'}
                            </Button>
                          )}
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </div>
              )}
            </>
          )}
        </Tab>
        <Tab eventKey="history" title="History" disabled={!task?.id}>
          {!task?.id ? (
            <Alert variant="info">Save the task first to view history.</Alert>
          ) : (
            <HistoryPanel
              composer={
                hasPermission('quantified:history_create') ? (
                  <form onSubmit={handleAddHistoryComment}>
                    <div className="tw-mb-3">
                      <Form.Label>Comment</Form.Label>
                      <Form.Control
                        as="textarea"
                        value={newHistoryComment}
                        onChange={(e) => setNewHistoryComment(e.target.value)}
                        // Shorter on a phone, where the composer sits above the timeline and a
                        // six-row textarea would push the history off screen on its own.
                        rows={3}
                        placeholder="Enter your comment here..."
                      />
                    </div>
                    <Button
                      type="submit"
                      variant="primary"
                      className="tw-w-full"
                      disabled={!newHistoryComment.trim() || isSubmittingHistory}
                    >
                      {isSubmittingHistory ? 'Adding...' : 'Add Comment'}
                    </Button>
                  </form>
                ) : (
                  <Alert variant="warning">
                    <i className="bi bi-exclamation-triangle tw-mr-2"></i>
                    You don't have permission to add comments to task history.
                  </Alert>
                )
              }
            >
                  {localHistory && localHistory.length > 0 ? (
                    <ListGroup>
                      {localHistory.sort((a, b) => 
                        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
                      ).map((history) => {
                        // Helper function to format history comments
                        const formatComments = () => {
                          if (!history.comments) return null;
                          
                          // For effort_applied entries, parse the form information
                          if (history.stage === 'effort_applied') {
                            const lines = history.comments.split('\n');
                            const formInfo = lines.find((line: string) => line.includes('from submission') || line.includes('submission'));
                            const otherInfo = lines.filter((line: string) => !line.includes('from submission') && !line.includes('submission'));
                            
                            return (
                              <div className="tw-mb-1">
                                {formInfo && (
                                  <div className="tw-mb-2">
                                    <strong className="tw-text-primary">
                                      <i className="bi bi-file-earmark-text tw-mr-1"></i>
                                      {formInfo}
                                    </strong>
                                  </div>
                                )}
                                {otherInfo.length > 0 && (
                                  <div className="tw-ml-3">
                                    {otherInfo.map((line: string, idx: number) => (
                                      <div key={idx} className="tw-text-sm tw-text-content-muted">{line}</div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          }

                          // For other entries, display comments with better formatting
                          const lines = history.comments.split('\n');
                          if (lines.length > 1) {
                            return (
                              <div className="tw-mb-1">
                                {lines.map((line: string, idx: number) => (
                                  <div key={idx} className={idx === 0 ? 'tw-font-semibold' : ''}>{line}</div>
                                ))}
                              </div>
                            );
                          }

                          return <p className="tw-mb-1">{history.comments}</p>;
                        };

                        return (
                          <ListGroup.Item key={history.id}>
                            <div className="tw-flex tw-justify-between tw-items-start">
                              <div className="tw-grow">
                                <h6 className="tw-mb-1">
                                  {history.summary}
                                </h6>
                                <p className="tw-mb-1 tw-text-content-muted">
                                  Stage: <StatusBadge status={history.stage} label={history.stage} />
                                </p>
                                {formatComments()}
                                <small className="tw-text-content-muted">
                                  {getTaskHistoryAttribution(history, task)}
                                </small>
                              </div>
                              <small className="tw-text-content-muted tw-ml-2">
                                {new Date(history.createdAt).toLocaleString()}
                              </small>
                            </div>
                          </ListGroup.Item>
                        );
                      })}
                    </ListGroup>
                  ) : (
                    <Alert variant="info">No history records found.</Alert>
                  )}
            </HistoryPanel>
          )}
        </Tab>
      </Tabs>
    </ResponsiveModal>
  );
};

export default CustomTaskModal; 